import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff } from 'lucide-react';
import { Logo } from '../components/Logo';
import { Alert, Button, Field, Input } from '../components/ui';
import { api, type Me } from '../lib/api';

/**
 * Login, and first-run bootstrap.
 *
 * Both live here because they are the same screen from the user's point of view: on a fresh
 * install there is nobody to log in as, so asking for credentials that cannot exist yet is a dead
 * end. The server decides which mode applies.
 *
 * Sized more generously than the rest of the admin on purpose. This is the one screen every user
 * meets before they know anything about the product, it is often the first thing seen on a phone,
 * and it holds four controls at most — there is no reason to make it dense.
 */
export function Login({ onAuthenticated }: { onAuthenticated: (me: Me) => void }) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [revealed, setRevealed] = useState(false);
  /** Set when the password was right but a second factor is needed. */
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState('');

  const { data: status, isLoading } = useQuery({
    queryKey: ['auth-status'],
    queryFn: () => api.get<{ needsBootstrap: boolean }>('/api/v1/auth/status'),
  });

  const bootstrap = useMutation({
    mutationFn: () => api.post<Me>('/api/v1/auth/bootstrap', { email, password, name }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['auth-status'] });
      login.mutate();
    },
  });

  const login = useMutation({
    mutationFn: () =>
      api.post<Me | { mfaRequired: true; challenge: string }>('/api/v1/auth/login', {
        email,
        password,
      }),
    onSuccess: (result) => {
      // A password alone is not a session when 2FA is on; the server returns a challenge and no
      // cookie, and the form swaps to a code prompt.
      if ('mfaRequired' in result) {
        setChallenge(result.challenge);
        return;
      }
      onAuthenticated(result);
    },
  });

  const verify = useMutation({
    mutationFn: () => api.post<Me>('/api/v1/auth/login/mfa', { challenge, code }),
    onSuccess: (me) => onAuthenticated(me),
  });

  if (isLoading) return null;

  const isBootstrap = status?.needsBootstrap ?? false;
  const pending = bootstrap.isPending || login.isPending || verify.isPending;
  const error = bootstrap.error ?? login.error ?? verify.error;

  // 16px, not the 14px used elsewhere. Anything smaller makes iOS Safari zoom the whole page in
  // when the field takes focus, and the user then has to pinch back out to reach the button.
  const field = 'h-11 text-base';

  return (
    <div className="flex min-h-screen items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-md">
        <div className="rounded-xl border border-border bg-card p-8 shadow-lg sm:p-10">
          <div className="flex flex-col items-center text-center">
            <Logo size={52} />
            <h1 className="mt-5 text-2xl font-semibold tracking-tight">klinkpbx</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {challenge
                ? 'Enter the code from your authenticator app.'
                : isBootstrap
                  ? 'Create the owner account for this PBX.'
                  : 'Sign in to continue.'}
            </p>
          </div>

          {challenge ? (
            <form
              className="mt-8 space-y-5"
              onSubmit={(e) => {
                e.preventDefault();
                verify.mutate();
              }}
            >
              <Field label="Six-digit code">
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputMode="numeric"
                  // Lets a password manager or iOS fill the code straight from the SMS/app prompt.
                  autoComplete="one-time-code"
                  maxLength={6}
                  className="h-14 text-center text-2xl tracking-[0.6em]"
                  autoFocus
                  required
                />
              </Field>

              {error ? <Alert>{(error as Error).message}</Alert> : null}

              <Button
                type="submit"
                className="h-11 w-full text-base"
                disabled={pending || code.length < 6}
              >
                {pending ? 'Checking…' : 'Verify'}
              </Button>
              <button
                type="button"
                className="w-full py-1 text-center text-sm text-muted-foreground underline-offset-4 hover:underline"
                onClick={() => {
                  setChallenge(null);
                  setCode('');
                }}
              >
                Back
              </button>
            </form>
          ) : (
            <form
              className="mt-8 space-y-5"
              onSubmit={(e) => {
                e.preventDefault();
                if (isBootstrap) bootstrap.mutate();
                else login.mutate();
              }}
            >
              {isBootstrap ? (
                <Field label="Your name">
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={field}
                    autoComplete="name"
                    autoFocus
                    required
                  />
                </Field>
              ) : null}

              <Field label="Email">
                <Input
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={field}
                  // Only when it is the first field: stealing focus from the name field on a
                  // fresh install would put the cursor in the wrong box.
                  autoFocus={!isBootstrap}
                  required
                />
              </Field>

              <Field
                label="Password"
                hint={isBootstrap ? 'At least 12 characters. A passphrase is fine.' : undefined}
              >
                <div className="relative">
                  <Input
                    type={revealed ? 'text' : 'password'}
                    autoComplete={isBootstrap ? 'new-password' : 'current-password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength={isBootstrap ? 12 : undefined}
                    // Room for the reveal button, so a long password never runs under it.
                    className={`${field} pr-11`}
                    required
                  />
                  {/*
                    Worth the extra control: the generated SIP-style passwords this product hands
                    out are 24 random characters, and typing one blind into a box that only says
                    "incorrect" afterwards is a genuinely bad first five minutes.
                  */}
                  <button
                    type="button"
                    onClick={() => setRevealed((on) => !on)}
                    // Not in the tab order: it sits between the password and the submit button,
                    // and tabbing into it is never what somebody signing in wants.
                    tabIndex={-1}
                    aria-label={revealed ? 'Hide password' : 'Show password'}
                    className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-md text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {revealed ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </button>
                </div>
              </Field>

              {error ? <Alert>{(error as Error).message}</Alert> : null}

              <Button type="submit" className="h-11 w-full text-base" disabled={pending}>
                {pending ? 'Working…' : isBootstrap ? 'Create account' : 'Sign in'}
              </Button>
            </form>
          )}
        </div>

        {isBootstrap ? (
          <p className="mt-5 text-center text-xs text-muted-foreground">
            This account owns the PBX. Add more people afterwards under System → Users.
          </p>
        ) : null}
      </div>
    </div>
  );
}
